import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ScanLine, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import dayjs from "dayjs";

export default function Redeem() {
  const [code, setCode] = useState("");
  const [checking, setChecking] = useState(false);
  const [redeeming, setRedeeming] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [redeemed, setRedeemed] = useState(null);

  const check = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setChecking(true); setError(""); setResult(null); setRedeemed(null);
    try {
      const { data } = await api.post("/redeem/check", { code });
      setResult(data);
    } catch (e2) {
      setError(formatApiError(e2.response?.data?.detail) || "Voucher tidak valid");
    } finally { setChecking(false); }
  };

  const doRedeem = async () => {
    setRedeeming(true);
    try {
      const { data } = await api.post("/redeem", { code });
      setRedeemed(data.voucher);
      setResult(null);
      toast.success("Voucher berhasil ditukar");
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail) || "Gagal menukar voucher");
      setResult(null);
      setError(formatApiError(e2.response?.data?.detail) || "Gagal menukar voucher");
    } finally { setRedeeming(false); }
  };

  return (
    <div className="space-y-6 max-w-xl">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Redeem Voucher</h1>
        <p className="text-sm text-zinc-500 mt-1">Masukkan kode voucher pelanggan untuk validasi dan penukaran.</p>
      </div>

      <form onSubmit={check} className="bg-white border border-zinc-200 rounded-2xl p-6 space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="voucher-code">Kode Voucher</Label>
          <div className="flex gap-2">
            <Input id="voucher-code" value={code} onChange={e => setCode(e.target.value.toUpperCase())}
              placeholder="LPT-XXXXXX" className="font-mono tracking-widest uppercase"
              data-testid="redeem-code-input" />
            <Button type="submit" disabled={checking || !code.trim()} className="bg-emerald-600 hover:bg-emerald-700 shrink-0" data-testid="redeem-check-button">
              <ScanLine size={16} className="mr-2" /> {checking ? "Memeriksa..." : "Cek"}
            </Button>
          </div>
        </div>
      </form>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-5 flex items-center gap-3" data-testid="redeem-error">
          <AlertCircle className="text-red-600 shrink-0" size={22} />
          <div className="text-sm font-medium text-red-800">{error}</div>
        </div>
      )}

      {result && (
        <div className="bg-white border-2 border-emerald-300 rounded-2xl p-6 space-y-4" data-testid="redeem-valid-card">
          <div className="flex items-center gap-2 text-emerald-700 font-bold text-lg">
            <CheckCircle2 size={22} /> Voucher Valid
          </div>
          <div className="space-y-1.5 text-sm">
            <div className="flex justify-between"><span className="text-zinc-500">Kupon</span><span className="font-semibold">{result.coupon.name}</span></div>
            <div className="flex justify-between"><span className="text-zinc-500">Diskon</span><span className="font-semibold text-emerald-700">{result.coupon.discount_label}</span></div>
            {result.coupon.min_transaction > 0 && (
              <div className="flex justify-between"><span className="text-zinc-500">Min. Transaksi</span><span>Rp{Number(result.coupon.min_transaction).toLocaleString("id-ID")}</span></div>
            )}
            <div className="flex justify-between"><span className="text-zinc-500">Pelanggan</span><span>{result.voucher.customer_name || "Anonim"}</span></div>
            <div className="flex justify-between"><span className="text-zinc-500">Berlaku sampai</span><span>{dayjs(result.voucher.expires_at).format("DD MMMM YYYY")}</span></div>
            <div className="flex justify-between"><span className="text-zinc-500">Kode</span><span className="font-mono font-bold">{result.voucher.unique_code}</span></div>
          </div>
          <Button onClick={doRedeem} disabled={redeeming} className="w-full bg-emerald-600 hover:bg-emerald-700 tap-target" data-testid="redeem-confirm-button">
            {redeeming ? "Memproses..." : "REDEEM VOUCHER"}
          </Button>
        </div>
      )}

      {redeemed && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 flex items-center gap-3" data-testid="redeem-success">
          <CheckCircle2 className="text-emerald-600 shrink-0" size={22} />
          <div>
            <div className="text-sm font-semibold text-emerald-800">Voucher {redeemed.unique_code} berhasil ditukar.</div>
            <div className="text-xs text-emerald-700 mt-0.5">Status: <Badge className="bg-blue-100 text-blue-800 border-0">Ditukar</Badge></div>
          </div>
        </div>
      )}
    </div>
  );
}
